import { globalStyle, keyframes, style } from '@vanilla-extract/css';

const spin = keyframes({
  from: { transform: 'rotate(0deg)' },
  to: { transform: 'rotate(360deg)' },
});

const shimmer = keyframes({
  '0%': { backgroundPosition: '-200% 0' },
  '100%': { backgroundPosition: '200% 0' },
});

export const container = style({
  width: '100%',
  boxSizing: 'border-box',
  flex: 'none',
});

export const playerWrapper = style({
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '12px',
  borderRadius: '8px',
  width: '100%',
  boxSizing: 'border-box',
  height: 'fit-content',
  position: 'relative',
});

export const previewBadge = style({
  flexShrink: 0,
  padding: '4px 8px',
  borderRadius: '9999px',
  backgroundColor: 'color-mix(in srgb, currentColor 14%, transparent)',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

export const thumbnail = style({
  width: '64px',
  height: '64px',
  borderRadius: '6px',
  objectFit: 'cover',
  flexShrink: 0,
});

export const playerContent = style({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: 0,
});

export const titleRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  width: '100%',
});

export const title = style({
  flex: 1,
  minWidth: 0,
  fontSize: '14px',
  fontWeight: 'bold',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

export const controlsRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const controlButton = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  transition: 'background-color 150ms',
  ':hover': {
    backgroundColor: 'rgba(128, 128, 128, 0.15)',
  },
  ':disabled': {
    opacity: 0.4,
    cursor: 'default',
  },
});

export const skipButton = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  padding: '2px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  transition: 'opacity 150ms',
  ':hover': {
    opacity: 0.7,
  },
  ':disabled': {
    opacity: 0.4,
    cursor: 'default',
  },
});

export const skipLabel = style({
  position: 'absolute',
  fontSize: '8px',
  fontWeight: 'bold',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
});

export const speedButton = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxSizing: 'border-box',
  minWidth: '50px',
  padding: '3px 6px',
  borderRadius: '9999px',
  border: 'none',
  background: 'transparent',
  fontSize: '12px',
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'background-color 150ms',
  ':hover': {
    backgroundColor: 'rgba(128, 128, 128, 0.15)',
  },
});

export const speedLabel = style({
  opacity: 0.7,
  transition: 'opacity 150ms',
  selectors: {
    [`${speedButton}:hover &, ${speedButton}[data-state='open'] &`]: {
      opacity: 1,
    },
  },
});

export const speedMenu = style({
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  minWidth: '88px',
  borderRadius: '6px',
  border: '1px solid #f3f4f6',
  backgroundColor: '#ffffff',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  color: '#111827',
  fontSize: '12px',
  fontWeight: 400,
  lineHeight: '16px',
  overflow: 'hidden',
});

export const speedMenuItem = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  minHeight: '25px',
  padding: '6px 10px',
  cursor: 'pointer',
  userSelect: 'none',
  outline: 'none',
  selectors: {
    '&[data-highlighted]': {
      backgroundColor: '#f3f4f6',
    },
  },
  ':hover': {
    backgroundColor: '#f3f4f6',
  },
});

export const speedMenuIndicator = style({
  display: 'flex',
  alignItems: 'center',
  width: '14px',
});

export const progressRow = style({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
});

export const timeLabel = style({
  fontSize: '12px',
  textAlign: 'center',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  opacity: 0.6,
});

export const progressBar = style({
  flex: 1,
  height: '8px',
  borderRadius: '9999px',
  overflow: 'hidden',
  cursor: 'pointer',
  backgroundColor: 'rgba(128, 128, 128, 0.3)',
});

export const progressFill = style({
  height: '100%',
  borderRadius: '9999px',
  transition: 'width 100ms linear',
});

export const waveformWrapper = style({
  flex: 1,
  minWidth: 0,
  position: 'relative',
});

export const waveformContainer = style({
  width: '100%',
});

export const spinnerIcon = style({
  animation: `${spin} 1s linear infinite`,
});

export const playButtonWrapper = style({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const spinnerOverlay = style({
  position: 'absolute',
  inset: 0,
  borderRadius: '50%',
  border: '2px solid transparent',
  borderTopColor: 'currentColor',
  animation: `${spin} 0.8s linear infinite`,
  pointerEvents: 'none',
  opacity: 0.7,
});

export const waveformLoading = style({
  position: 'absolute',
  inset: 0,
  borderRadius: '4px',
  background:
    'linear-gradient(90deg, rgba(128,128,128,0.08) 25%, rgba(128,128,128,0.18) 50%, rgba(128,128,128,0.08) 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.5s ease-in-out infinite`,
});

export const detailedContainer = style({ containerType: 'inline-size' });

export const detailedWrapper = style({
  padding: '16px',
  gap: '20px',
  '@container': {
    '(max-width: 480px)': {
      display: 'grid',
      gridTemplateColumns: '64px minmax(0, 1fr)',
      gap: '16px',
    },
    '(max-width: 380px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
});

export const detailedArtwork = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '180px',
  height: '180px',
  borderRadius: '16px',
  flexShrink: 0,
  objectFit: 'cover',
  backgroundColor: '#f3f4f6',
  color: '#6b7280',
  '@container': {
    '(max-width: 480px)': { width: '64px', height: '64px', borderRadius: '12px' },
    '(max-width: 380px)': { display: 'none' },
  },
});

export const detailedContent = style({
  gap: '20px',
  '@container': { '(max-width: 480px)': { display: 'contents' } },
});

export const detailedControls = style({ gap: '12px', minWidth: 0 });

export const detailedMetadata = style({ flex: 1, minWidth: 0 });

export const detailedTitle = style({
  margin: 0,
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: 1.4,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  '@container': { '(max-width: 480px)': { fontSize: '14px' } },
});

export const detailedSubtitle = style({ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' });

export const detailedProgress = style({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px',
  '@container': { '(max-width: 480px)': { gridColumn: '1 / -1' } },
});

export const detailedWaveform = style({ gridColumn: '1 / -1', gridRow: 1 });
export const detailedCurrentTime = style({ gridColumn: 1, gridRow: 2, textAlign: 'left', fontSize: '14px' });
export const detailedDuration = style({ gridColumn: 2, gridRow: 2, textAlign: 'right', fontSize: '14px' });

globalStyle(`${detailedControls} ${speedButton}`, {
  border: '1px solid #e5e7eb',
  minWidth: '44px',
  minHeight: '32px',
  fontSize: '14px',
});
